import React from "react";

import type { JSX } from "react";
import { forwardRef } from "react";
import { getOptimizedMediaUrl as baseGetOptimizedMediaUrl } from "~/components/ui/Image";

/** Compression level accepted by the deco image CDN. */
export type QualityOptions = "low" | "medium" | "high";

export type Props = Omit<
  React.JSX.IntrinsicElements["img"],
  "width" | "height" | "preload"
> & {
  src: string;
  /** @description Improves Web Vitals (CLS|LCP) */
  width: number;
  /** @description Improves Web Vitals (CLS|LCP) */
  height?: number;
  /** @description Web Vitals (LCP). Adds a link[rel="preload"] tag in head. Use one preload per page for better performance */
  preload?: boolean;
  /** @description Improves Web Vitals (LCP). Use high for LCP image. Auto for other images */
  fetchPriority?: "high" | "low" | "auto";
  /** @description Object-fit */
  fit?: FitOptions;
  /** @description Media query for preload link */
  media?: string;

  factors?: number[];
  /** @description Nivel de compressao. Use "high" em banners com texto. */
  quality?: QualityOptions;
};

const FACTORS = [1, 2];

export type FitOptions = "contain" | "cover";

export const getOptimizedMediaUrl = ({
  originalSrc,
  width,
  height,
  factor,
  fit = "cover",
  quality,
}: {
  originalSrc: string;
  width: number;
  height?: number;
  factor: number;
  fit?: FitOptions;
  quality?: QualityOptions;
}) => {
  if (originalSrc.startsWith("data:")) return originalSrc;

  // Already an optimizer URL (`decoims.com/image?src=…`): re-running it would
  // strip the query and produce `…/image?…&src=image`.
  if (originalSrc.includes("/image?")) return originalSrc;

  // Delegate to the framework optimizer (decoims.com/image CDN). It applies
  // the factor by receiving the already-multiplied width/height — the legacy
  // `/live/invoke/website/loaders/image.ts` route isn't served here, so the
  // old PATH produced broken images. `~/components/ui/Image` normalizes the
  // decoims/decoassets aliases first, so CDN-hosted sources get resized too
  // instead of being passed through at full weight.
  const url = baseGetOptimizedMediaUrl({
    originalSrc,
    width: Math.trunc(factor * width),
    height: height ? Math.trunc(factor * height) : undefined,
    fit,
  });

  // The framework only exposes quality as a site-wide default
  // (`registerImageQuality`); add the per-image override by hand.
  return quality && url.includes("/image?")
    ? url.replace("&src=", `&quality=${quality}&src=`)
    : url;
};

export const getSrcSet = (
  src: string,
  width: number,
  height?: number,
  fit?: FitOptions,
  factors?: number[],
  quality?: QualityOptions
) => {
  const factorsToUse = factors ?? FACTORS;

  if (factorsToUse.length <= 1) {
    return getOptimizedMediaUrl({
      originalSrc: src,
      width,
      height,
      factor: factorsToUse[0] ?? 1,
      fit,
      quality,
    });
  }

  return (factors ?? FACTORS)
    .map(
      (factor) =>
        `${getOptimizedMediaUrl({
          originalSrc: src,
          width,
          height,
          factor,
          fit,
          quality,
        })} ${Math.trunc(factor * width)}w`
    )
    .join(", ");
};

const CustomImage = forwardRef<HTMLImageElement, Props>((props, ref) => {
  // Custom props are not valid <img> attributes: keep them out of the spread.
  const {
    preload,
    loading = "lazy",
    factors: _factors,
    quality: _quality,
    fit: _fit,
    media: _media,
    ...imgProps
  } = props;

  if (!props.height) {
    console.warn(
      `Missing height. This image will NOT be optimized: ${props.src}`
    );
  }

  const srcSet = getSrcSet(
    props.src,
    props.width,
    props.height,
    props.fit,
    props.factors,
    props.quality
  );

  const isSingleFactor = (props.factors ?? FACTORS).length <= 1;

  const linkProps = {
    imagesrcset: isSingleFactor ? undefined : srcSet,
    imagesizes: props.sizes,
    fetchpriority: props.fetchPriority,
    media: props.media,
  };

  return (
    <>
      {preload && (
        <>
          <link as="image" rel="preload" href={props.src} {...linkProps} />
        </>
      )}
      <img
        {...(imgProps as React.JSX.IntrinsicElements["img"])}
        src={isSingleFactor ? srcSet : props.src}
        srcSet={isSingleFactor ? undefined : srcSet}
        loading={loading}
        ref={ref}
      />
    </>
  );
});

export default CustomImage;
